import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { useActiveFlat } from "./lib/active-flat";
import type { ReactNode } from "react";

import { LoginPage } from "./pages/LoginPage";
import { VerifyPage } from "./pages/VerifyPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { JoinFlatPage } from "./pages/JoinFlatPage";
import { CreateOrJoinFlatPage } from "./pages/CreateOrJoinFlatPage";
import { DashboardPage } from "./pages/DashboardPage";
import { ExpensesPage } from "./pages/ExpensesPage";
import { ChoresPage } from "./pages/ChoresPage";
import { ShoppingPage } from "./pages/ShoppingPage";
import { InventoryPage } from "./pages/InventoryPage";
import { BoardPage } from "./pages/BoardPage";
import { SettingsPage } from "./pages/SettingsPage";
import { NfcPage } from "./pages/NfcPage";
import { AppShell } from "./components/AppShell";

function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();
  if (auth.status === "loading") return <FullPageSpinner />;
  if (auth.status === "anonymous") return <Navigate to="/login" replace />;
  if (!auth.user.name) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

function RequireFlat({ children }: { children: ReactNode }) {
  const flat = useActiveFlat();
  if (flat.status === "loading") return <FullPageSpinner />;
  if (flat.status === "none") return <Navigate to="/flat/new" replace />;
  return <AppShell>{children}</AppShell>;
}

function FullPageSpinner() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/verify" element={<VerifyPage />} />
      <Route path="/invite/:code" element={<JoinFlatPage />} />
      <Route
        path="/onboarding"
        element={
          <RequireAuthOnly>
            <OnboardingPage />
          </RequireAuthOnly>
        }
      />
      <Route
        path="/flat/new"
        element={
          <RequireAuth>
            <CreateOrJoinFlatPage />
          </RequireAuth>
        }
      />
      <Route path="/nfc" element={<NfcPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <RequireFlat>
              <DashboardPage />
            </RequireFlat>
          </RequireAuth>
        }
      />
      <Route
        path="/expenses"
        element={
          <RequireAuth>
            <RequireFlat>
              <ExpensesPage />
            </RequireFlat>
          </RequireAuth>
        }
      />
      <Route
        path="/chores"
        element={
          <RequireAuth>
            <RequireFlat>
              <ChoresPage />
            </RequireFlat>
          </RequireAuth>
        }
      />
      <Route
        path="/shopping"
        element={
          <RequireAuth>
            <RequireFlat>
              <ShoppingPage />
            </RequireFlat>
          </RequireAuth>
        }
      />
      <Route
        path="/inventory"
        element={
          <RequireAuth>
            <RequireFlat>
              <InventoryPage />
            </RequireFlat>
          </RequireAuth>
        }
      />
      <Route
        path="/board"
        element={
          <RequireAuth>
            <RequireFlat>
              <BoardPage />
            </RequireFlat>
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <RequireFlat>
              <SettingsPage />
            </RequireFlat>
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function RequireAuthOnly({ children }: { children: ReactNode }) {
  // Same as RequireAuth but does not redirect to /onboarding when name is null.
  const auth = useAuth();
  if (auth.status === "loading") return <FullPageSpinner />;
  if (auth.status === "anonymous") return <Navigate to="/login" replace />;
  return <>{children}</>;
}
